/**
 * Calendario attività UOC - template settimanali e modifiche (perpetue/estemporanee)
 */

import {
  getState,
  setCalendarWeekStart,
  canEditCalendar,
  isWeekLocked,
} from '../store.js';
import { sortWeekdaysCalendarOrder } from '../types.js';
import {
  formatLocalISODate,
  formatDateShort,
  ensurePlannerSlotName,
  getISOWeekDateRangeStr,
  getUniqueSlotName,
  getMondayOfISOWeek,
  getMondayOfWeek,
} from '../utils.js';
import { escapeHtmlText, escapeHtmlAttr } from '../calendar/domSafe.js';
import {
  SHIFT_PLANNER_LOCKED_CALENDAR_MSG,
  weekdayCheckboxesHtml,
  WEEKDAYS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT,
  TIME_SLOT_ORDER_CALENDAR,
  TIME_SLOT_LABELS,
  UOC_LABELS,
  SLOT_CATEGORY_LABELS,
  ACTIVITY_TYPE_LABELS,
  LUN_VEN,
  shiftPlannerLockedWeeksTouchRange,
  formatTemplateWeekdaysShort,
  formatTemplatePeriod,
  applyCategoryToUocSelect,
  requiredUocIdForCategory,
  collectConflictingTemplateNames,
  buildCalendarWeekPreviewModel,
  weekCellBadgeHtml,
} from '../calendar/calendarGrid.js';
import { showAddSlotPopup, showCessationPopup, showEditTemplatePopup } from '../calendar/calendarModals.js';
import {
  calendarCreateTemplate,
  calendarDeleteTemplate,
  calendarCreateModification,
  calendarDeleteModification,
} from '../calendar/calendarFacade.js';

/**
 * Listener delegato sul contenitore vista: su Firefox/alcuni touchscreen i click
 * sulla tabella non sempre innescano i listener attaccati a ogni badge.
 */
function ensureCalendarPlannerDelegatedClicks(mainEl) {
  if (mainEl.dataset.calendarPlannerDelegated === '1') return;
  mainEl.dataset.calendarPlannerDelegated = '1';
  mainEl.addEventListener(
    'click',
    (e) => {
      const root = e.target.closest?.('.calendar-planner-root');
      if (!root || !mainEl.contains(root)) return;
      const badge = e.target.closest?.('.week-slot-badge');
      if (!badge || !root.contains(badge)) return;

      e.preventDefault();
      e.stopPropagation();

      if (!canEditCalendar()) {
        alert('Non hai i permessi per modificare il calendario (serve ruolo organizer, direttore o admin).');
        return;
      }

      const refresh = () => renderCalendarPlanner(mainEl);
      const date = (badge.getAttribute('data-date') ?? badge.dataset.date ?? '').slice(0, 10).trim();
      const timeSlot = (badge.getAttribute('data-timeslot') ?? badge.dataset.timeslot ?? '').trim();
      const rawCat = badge.getAttribute('data-category') ?? badge.dataset.category;
      const category = String(rawCat ?? '')
        .trim()
        .toLowerCase()
        .slice(0, 64);
      const uocHint = (badge.getAttribute('data-uocid') ?? badge.dataset.uocid ?? '').trim();
      const uocIdResolved = uocHint || requiredUocIdForCategory(category) || '';
      const label = badge.textContent ?? '';
      const oneOffModId = (
        badge.getAttribute('data-oneoff-mod-id') ||
        badge.dataset.oneoffModId ||
        ''
      ).trim();

      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        alert('Impossibile leggere la data dello slot. Ricarica la pagina se il problema si ripete.');
        return;
      }
      if (!timeSlot || !category) {
        alert('Dati incompleti per questo slot (fascia o categoria): impossibile rimuovere.');
        return;
      }
      if (!oneOffModId && !uocIdResolved) {
        alert('Impossibile determinare la UOC per questo slot (categoria sconosciuta o dati incompleti).');
        return;
      }
      if (!confirm(`Eliminare lo slot "${label}" del ${date} (${TIME_SLOT_LABELS[timeSlot] || timeSlot})?`))
        return;
      const mondayWeek = getMondayOfWeek(date);
      if (isWeekLocked(mondayWeek)) {
        alert(SHIFT_PLANNER_LOCKED_CALENDAR_MSG);
        return;
      }
      if (oneOffModId) {
        calendarDeleteModification(oneOffModId).then(refresh).catch((err) => alert('Errore: ' + err.message));
      } else {
        calendarCreateModification({
          modificationType: 'remove',
          scope: 'one_off',
          dateSpecific: date,
          dateFrom: date,
          timeSlot,
          category,
          uocId: uocIdResolved,
          name:
            (badge.getAttribute('data-name') != null ? badge.getAttribute('data-name') : badge.dataset.name) ||
            null,
          activityType: 'clinico',
        })
          .then(refresh)
          .catch((err) => alert('Errore: ' + err.message));
      }
    },
    true,
  );
}

/** Tab attiva Calendario (persiste tra re-render). */
let calendarPlannerTab = 'preview';

/**
 * Ogni `setState` → `render()` → qui `innerHTML` distrugge il DOM.
 * Preserviamo bozza "Nuovo slot ricorrente" + focus per non perdere il cursore nel campo Nome.
 */
function captureRecurringTemplateFormDraft(container) {
  const nameEl = container.querySelector('#template-name');
  if (!nameEl) return null;
  const ae = document.activeElement;
  const root = container.querySelector('.calendar-planner-root');
  const newTemplateCard = nameEl.closest('.card');
  const hadFocusInNewTemplateCard = Boolean(ae && newTemplateCard?.contains(ae));
  let focusFieldId = '';
  let focusTplWdValue = '';
  if (ae && root?.contains(ae)) {
    if (ae.id && /^[a-zA-Z0-9_-]+$/.test(ae.id)) focusFieldId = ae.id;
    else if (ae.matches?.('.tpl-wd')) focusTplWdValue = String(ae.value || '');
  }
  return {
    name: nameEl.value,
    activityType: container.querySelector('#template-activity-type')?.value ?? '',
    category: container.querySelector('#template-category')?.value ?? '',
    weekdays: [...container.querySelectorAll('.tpl-wd:checked')].map((el) => el.value),
    dateFrom: container.querySelector('#template-date-from')?.value ?? '',
    dateTo: container.querySelector('#template-date-to')?.value ?? '',
    timeSlot: container.querySelector('#template-timeslot')?.value ?? '',
    focusFieldId,
    focusTplWdValue,
    hadFocusInNewTemplateCard,
  };
}

function restoreRecurringTemplateFormDraft(container, draft) {
  if (!draft) return;
  const setVal = (id, v) => {
    const el = container.querySelector(`#${id}`);
    if (el && v != null) el.value = v;
  };
  setVal('template-name', draft.name);
  setVal('template-activity-type', draft.activityType);
  setVal('template-category', draft.category);
  setVal('template-date-from', draft.dateFrom);
  setVal('template-date-to', draft.dateTo);
  setVal('template-timeslot', draft.timeSlot);
  const wdSet = new Set(draft.weekdays || []);
  container.querySelectorAll('.tpl-wd').forEach((cb) => {
    cb.checked = wdSet.has(cb.value);
  });

  const tplCat = container.querySelector('#template-category');
  const tplUoc = container.querySelector('#template-uoc');
  if (tplCat && tplUoc) applyCategoryToUocSelect(tplCat.value, tplUoc);

  const reapplyFocus = () => {
    if (!draft.hadFocusInNewTemplateCard && !draft.focusFieldId && !draft.focusTplWdValue) return;
    let toFocus = null;
    if (draft.focusFieldId) toFocus = container.querySelector(`#${draft.focusFieldId}`);
    if (!toFocus && draft.focusTplWdValue) {
      toFocus = container.querySelector(
        `.tpl-wd[value="${CSS.escape(String(draft.focusTplWdValue))}"]`,
      );
    }
    if (!toFocus && draft.hadFocusInNewTemplateCard) toFocus = container.querySelector('#template-name');
    if (toFocus && typeof toFocus.focus === 'function') {
      toFocus.focus();
      if (toFocus.id === 'template-name' && typeof draft.name === 'string' && toFocus.setSelectionRange) {
        const len = draft.name.length;
        try {
          toFocus.setSelectionRange(len, len);
        } catch (_) {}
      }
    }
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(reapplyFocus);
  });
}

export function renderCalendarPlanner(container, options = {}) {
  const formDraft =
    options.resetNewTemplateForm || !container.querySelector('#template-name')
      ? null
      : captureRecurringTemplateFormDraft(container);

  const preservedScroll = [...container.querySelectorAll('.table-wrapper')].map((el) => ({
    top: el.scrollTop,
    left: el.scrollLeft,
  }));

  const state = getState();
  const { calendarTemplates, uocs } = state;
  const uocsList = uocs.length
    ? uocs
    : [
        { id: 'uoc_anestesia_ti', name: UOC_LABELS.uoc_anestesia_ti },
        { id: 'uos_blocco_operatorio', name: UOC_LABELS.uos_blocco_operatorio },
        { id: 'uos_medicina_dolore', name: UOC_LABELS.uos_medicina_dolore },
      ];

  if (calendarPlannerTab === 'mods' || calendarPlannerTab === 'spot') calendarPlannerTab = 'preview';
  const tab = calendarPlannerTab;

  const weekModel = buildCalendarWeekPreviewModel(state);
  const { weekStart, weekDates, slotsByCell, weekLabel, isoWeek, currentYear, currentWeekNum, balance } = weekModel;

  const years = [];
  for (let y = 2022; y <= 2032; y++) years.push(y);
  const canEdit = canEditCalendar();
  const balanceClass = balance.diff >= 0 ? 'balance-ok' : 'balance-deficit';
  const balanceText =
    balance.diff >= 0
      ? `Bilancio: +${balance.diff.toFixed(0)} h`
      : `Bilancio: ${balance.diff.toFixed(0)} h — richiederà straordinari`;

  container.innerHTML = `
    <div class="card calendar-planner-root">
      <div class="card-title">Calendario attività UOC</div>
      <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
        Base per il Piano turni: qui definisci gli <strong>slot ricorrenti</strong> (ogni settimana) e le <strong>modifiche</strong> (regole da data o <strong>spot</strong> su una sola data). In anteprima sono mostrati anche gli slot persistiti dal database per mantenere allineamento completo con il Piano turni.
      </p>

      <div class="calendar-tabs" role="tablist" aria-label="Sezioni calendario">
        <button type="button" class="calendar-tab ${tab === 'preview' ? 'is-active' : ''}" data-cal-tab="preview" role="tab" aria-selected="${tab === 'preview'}">Anteprima settimana</button>
        <button type="button" class="calendar-tab ${tab === 'recurring' ? 'is-active' : ''}" data-cal-tab="recurring" role="tab" aria-selected="${tab === 'recurring'}">Slot ricorrenti</button>
      </div>

      <div class="calendar-tab-panel ${tab === 'preview' ? 'is-active' : ''}" data-panel="preview" role="tabpanel">
        <div class="card-title" style="margin-top: 0.5rem;">Settimana in anteprima</div>
        <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap;">
          <button type="button" class="btn btn-secondary btn-sm" id="week-prev">‹ Sett. prec.</button>
          <div>
            <strong id="week-label">${weekLabel}</strong>
            <div style="font-size: 0.8rem; color: var(--text-secondary);">${isoWeek}</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" id="week-next">Sett. succ. ›</button>
          <div style="margin-left: 1rem; padding-left: 1rem; border-left: 1px solid var(--border); display: flex; gap: 0.5rem; align-items: flex-end; flex-wrap: wrap;">
            <div>
              <label style="font-size: 0.75rem; color: var(--text-secondary);">Anno</label>
              <select id="calendar-jump-year" style="min-width: 4rem;">
                ${years.map((y) => `<option value="${y}" ${y === currentYear ? 'selected' : ''}>${y}</option>`).join('')}
              </select>
            </div>
            <div>
              <label style="font-size: 0.75rem; color: var(--text-secondary);">Sett. ISO</label>
              <select id="calendar-jump-week" style="min-width: 18rem;">
                ${Array.from({ length: 53 }, (_, i) => {
                  const w = i + 1;
                  const rangeStr = getISOWeekDateRangeStr(currentYear, w);
                  return `<option value="${w}" ${w === currentWeekNum ? 'selected' : ''}>W${String(w).padStart(2, '0')} (${rangeStr})</option>`;
                }).join('')}
              </select>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" id="calendar-jump-btn">Vai</button>
          </div>
        </div>
        <div class="balance-indicator ${balanceClass}" title="Domanda = slot ufficiali della settimana ISO (calendario+DB, deduplicati), esclusi reperibilità e Direzione: somma pesi fascia (M/P=1, Pr=1,2, N=2), ogni unità di slot = 6h. Disponibilità = per ogni medico attivo senza Abilità Direzione: (5 giorni − festivi) × (debito orario settimanale da Medici ÷ 5) − assenze giustificate. Slot disponibili = disponibilità ore ÷ 6h.">
          Ore da coprire: <strong>${balance.scheduled.toFixed(0)} h</strong> | Disponibilità: <strong>${balance.availability.toFixed(0)} h</strong> | ${balanceText}
          <span style="opacity: 0.9;"> &nbsp;|&nbsp; Slot: <strong>${balance.scheduledSlots.toFixed(1)}</strong> da coprire | <strong>${balance.availabilitySlots.toFixed(1)}</strong> disponibili | Bilancio: <strong>${balance.balanceSlots >= 0 ? '+' : ''}${balance.balanceSlots.toFixed(1)}</strong></span>
        </div>
        <p style="font-size: 0.82rem; color: var(--text-secondary); margin-bottom: 0.75rem;">
          <strong>Spot:</strong> clic su una <strong>cella vuota</strong> per aggiungere uno slot solo in quella data; clic sul <strong>badge</strong> per rimuovere (spot o eccezione).
        </p>
        <div class="table-wrapper" style="margin-bottom: 1rem;">
          <table class="calendar-summary">
            <thead>
              <tr>
                <th>Fascia</th>
                ${weekDates
                  .map(
                    (d, i) =>
                      `<th title="${escapeHtmlAttr(WEEKDAY_LABELS[WEEKDAYS[i]] || '')}">${escapeHtmlText(formatDateShort(d))}<br><small>${escapeHtmlText(WEEKDAY_SHORT[WEEKDAYS[i]] || WEEKDAYS[i] || '')}</small></th>`,
                  )
                  .join('')}
              </tr>
            </thead>
            <tbody>
              ${TIME_SLOT_ORDER_CALENDAR.map((ts) => {
                const tsLabel = escapeHtmlText(TIME_SLOT_LABELS[ts] || ts);
                return `
                <tr>
                  <td><strong>${tsLabel}</strong></td>
                  ${weekDates
                    .map((d, i) => {
                      const key = `${d}-${ts}`;
                      const slots = slotsByCell[key] || [];
                      const wd = WEEKDAYS[i];
                      const cellInner = slots.length
                        ? slots.map((s) => weekCellBadgeHtml(s, canEdit)).join(' ')
                        : '<span class="week-cell-empty">—</span>';
                      const titleEsc = escapeHtmlAttr(
                        canEdit ? (slots.length ? 'Click sul badge per rimuovere' : 'Click per aggiungere spot') : '',
                      );
                      return `
                    <td class="summary-cell week-cell" data-date="${escapeHtmlAttr(d)}" data-timeslot="${escapeHtmlAttr(ts)}" data-weekday="${escapeHtmlAttr(wd)}" title="${titleEsc}">
                      ${cellInner}
                    </td>`;
                    })
                    .join('')}
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>

      <div class="calendar-tab-panel ${tab === 'recurring' ? 'is-active' : ''}" data-panel="recurring" role="tabpanel">
        <div class="card-title" style="margin-top: 0.5rem;">Slot ricorrenti (template)</div>
        <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem;">
          Ogni riga è uno <strong>unico slot ricorrente</strong>: stesso nome, UOC, fascia e categoria, ripetuto nei <strong>giorni selezionati</strong> (es. TI mattino tutti i giorni, oppure Cure palliative solo lun–mer–ven). Opzionale: <strong>Attivo dal</strong> / <strong>Fino al incluso</strong> per limitare il periodo (vuoto = senza limite). La tabella resta compatta rispetto a una riga per ogni giorno.
        </p>
        <div class="card" style="background: var(--bg-secondary); border-color: var(--border); margin-top: 0; ${canEdit ? '' : 'display: none;'}">
          <div class="card-title" style="margin-top: 0; font-size: 1.1rem;">Nuovo slot ricorrente</div>
          <div style="display: flex; flex-wrap: wrap; gap: 0.65rem; align-items: flex-end;">
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">Nome</label>
              <input type="text" id="template-name" placeholder="es. TI Mattino" style="width: 7.5rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
            </div>
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">Tipo</label>
              <select id="template-activity-type" style="width: 7.5rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
                ${Object.entries(ACTIVITY_TYPE_LABELS)
                  .map(([k, v]) => `<option value="${escapeHtmlAttr(k)}">${escapeHtmlText(v)}</option>`)
                  .join('')}
              </select>
            </div>
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">Categoria</label>
              <select id="template-category" style="min-width: 11rem; max-width: 20rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
                ${Object.entries(SLOT_CATEGORY_LABELS)
                  .map(([k, v]) => `<option value="${escapeHtmlAttr(k)}">${escapeHtmlText(v)}</option>`)
                  .join('')}
              </select>
            </div>
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">UOC (derivata)</label>
              <select id="template-uoc" disabled title="La UOC è fissata dalla categoria scelta." style="min-width: 11rem; max-width: 18rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
                ${uocsList.map((u) => `<option value="${escapeHtmlAttr(u.id)}">${escapeHtmlText(u.name)}</option>`).join('')}
              </select>
            </div>
            <div style="flex: 1 1 100%;">
              <label style="font-size: 0.85rem; display: block; margin-bottom: 0.25rem;">Giorni della settimana</label>
              <div class="template-wd-row" style="display: flex; flex-wrap: wrap; align-items: center;">${weekdayCheckboxesHtml('tpl-wd', LUN_VEN)}</div>
            </div>
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">Attivo dal (opz.)</label>
              <input type="date" id="template-date-from" style="width: 8rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
            </div>
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">Fino al incluso (opz.)</label>
              <input type="date" id="template-date-to" style="width: 8rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
            </div>
            <div style="min-width: 0;">
              <label style="font-size: 0.85rem; display: block;">Fascia</label>
              <select id="template-timeslot" style="width: 8.5rem; padding: 0.35rem 0.5rem; font-size: 0.95rem;">
                ${Object.entries(TIME_SLOT_LABELS)
                  .map(([k, v]) => `<option value="${escapeHtmlAttr(k)}">${escapeHtmlText(v)}</option>`)
                  .join('')}
              </select>
            </div>
            <button type="button" class="btn btn-primary btn-sm" id="btn-add-template" style="padding: 0.4rem 0.75rem; font-size: 0.95rem;">+ Aggiungi slot</button>
          </div>
        </div>
        <div class="table-wrapper" style="margin-top: 1rem; margin-bottom: 1rem;">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>UOC</th>
                <th>Giorni</th>
                <th>Periodo</th>
                <th>Fascia</th>
                <th>Categoria</th>
                <th></th>
              </tr>
            </thead>
            <tbody id="templates-tbody">
              ${calendarTemplates
                .map((t) => {
                  const nameCell = escapeHtmlText(t.name || '—');
                  const typeCell = escapeHtmlText(ACTIVITY_TYPE_LABELS[t.activityType] || t.activityType || 'clinico');
                  const uocCell = escapeHtmlText(UOC_LABELS[t.uocId] || t.uocId);
                  const daysCell = escapeHtmlText(formatTemplateWeekdaysShort(t));
                  const periodCell = escapeHtmlText(formatTemplatePeriod(t));
                  const slotCell = escapeHtmlText(TIME_SLOT_LABELS[t.timeSlot] || t.timeSlot);
                  const catCell = escapeHtmlText(SLOT_CATEGORY_LABELS[t.category] || t.category);
                  return `
                <tr>
                  <td>${nameCell}</td>
                  <td>${typeCell}</td>
                  <td>${uocCell}</td>
                  <td style="font-size:0.88rem;white-space:normal;max-width:10rem;">${daysCell}</td>
                  <td style="font-size:0.82rem;">${periodCell}</td>
                  <td>${slotCell}</td>
                  <td>${catCell}</td>
                  <td>
                    ${canEdit ? `<button type="button" class="btn btn-secondary btn-sm btn-edit-template" data-id="${escapeHtmlAttr(t.id)}" title="Modifica">Modifica</button>
                    <button type="button" class="btn btn-secondary btn-sm btn-cessa-template" data-id="${escapeHtmlAttr(t.id)}" title="Cessa da data">Cessa da...</button>
                    <button type="button" class="btn btn-secondary btn-sm btn-del-template" data-id="${escapeHtmlAttr(t.id)}">Rimuovi</button>` : ''}
                  </td>
                </tr>`;
                })
                .join('')}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  `;

  [...container.querySelectorAll('.table-wrapper')].forEach((el, i) => {
    const s = preservedScroll[i];
    if (s) {
      el.scrollTop = s.top;
      el.scrollLeft = s.left;
    }
  });

  container.querySelectorAll('[data-cal-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      calendarPlannerTab = btn.dataset.calTab || 'preview';
      renderCalendarPlanner(container);
    });
  });

  const tplCat = container.querySelector('#template-category');
  const tplUoc = container.querySelector('#template-uoc');
  if (tplCat && tplUoc) {
    const syncTplUoc = () => applyCategoryToUocSelect(tplCat.value, tplUoc);
    tplCat.addEventListener('change', syncTplUoc);
    syncTplUoc();
  }

  const goWeek = (delta) => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + delta * 7);
    setCalendarWeekStart(formatLocalISODate(d));
    renderCalendarPlanner(container);
  };
  container.querySelector('#week-prev')?.addEventListener('click', () => goWeek(-1));
  container.querySelector('#week-next')?.addEventListener('click', () => goWeek(1));

  const updateWeekOptions = () => {
    const yearSelect = container.querySelector('#calendar-jump-year');
    const weekSelect = container.querySelector('#calendar-jump-week');
    if (!yearSelect || !weekSelect) return;
    const year = parseInt(yearSelect.value || new Date().getFullYear(), 10);
    const currentWeek = weekSelect.value;
    weekSelect.innerHTML = Array.from({ length: 53 }, (_, i) => {
      const w = i + 1;
      const rangeStr = getISOWeekDateRangeStr(year, w);
      return `<option value="${w}" ${w == currentWeek ? 'selected' : ''}>W${String(w).padStart(2, '0')} (${rangeStr})</option>`;
    }).join('');
  };
  container.querySelector('#calendar-jump-year')?.addEventListener('change', updateWeekOptions);

  container.querySelector('#calendar-jump-btn')?.addEventListener('click', () => {
    const year = parseInt(container.querySelector('#calendar-jump-year')?.value || new Date().getFullYear(), 10);
    const weekNum = parseInt(container.querySelector('#calendar-jump-week')?.value || 1, 10);
    const monday = getMondayOfISOWeek(year, weekNum);
    setCalendarWeekStart(monday);
    renderCalendarPlanner(container);
  });

  const refresh = () => renderCalendarPlanner(container);

  ensureCalendarPlannerDelegatedClicks(container);

  container.querySelectorAll('.week-cell').forEach((cell) => {
    cell.addEventListener('click', (e) => {
      if (e.target.closest('.week-slot-badge')) return;
      if (!canEdit) return;
      const date = cell.dataset.date;
      const timeSlot = cell.dataset.timeslot;
      const weekday = cell.dataset.weekday;
      showAddSlotPopup(container, { date, timeSlot, weekday }, uocsList, refresh);
    });
  });

  container.querySelector('#btn-add-template')?.addEventListener('click', async () => {
    const rawName = container.querySelector('#template-name').value.trim();
    const activityType = container.querySelector('#template-activity-type').value;
    const category = container.querySelector('#template-category').value;
    const uocId = requiredUocIdForCategory(category);
    if (!uocId) {
      alert('Categoria senza UOC mappata: aggiorna l’app o scegli un’altra categoria.');
      return;
    }
    const timeSlot = container.querySelector('#template-timeslot').value;
    const dateFrom = container.querySelector('#template-date-from').value?.trim().slice(0, 10) || null;
    const dateTo = container.querySelector('#template-date-to').value?.trim().slice(0, 10) || null;
    const weekdays = sortWeekdaysCalendarOrder([...container.querySelectorAll('.tpl-wd:checked')].map((el) => el.value));
    if (!weekdays.length) {
      alert('Seleziona almeno un giorno della settimana.');
      return;
    }
    if (dateFrom && dateTo && dateTo < dateFrom) {
      alert('La data fine non può precedere la data di inizio.');
      return;
    }
    const baseLabel = ensurePlannerSlotName(category, rawName, timeSlot, activityType);
    const existingNames = collectConflictingTemplateNames(timeSlot, weekdays, dateFrom, dateTo, null);
    const name = getUniqueSlotName(baseLabel, existingNames) || baseLabel;
    if (dateFrom && shiftPlannerLockedWeeksTouchRange(dateFrom, dateTo || dateFrom)) {
      alert(SHIFT_PLANNER_LOCKED_CALENDAR_MSG);
      return;
    }
    try {
      await calendarCreateTemplate({
        name,
        activityType,
        uocId,
        weekdays,
        timeSlot,
        category,
        dateFrom,
        dateTo,
      });
      renderCalendarPlanner(container, { resetNewTemplateForm: true });
    } catch (e) {
      alert('Errore: ' + e.message);
    }
  });

  container.querySelectorAll('.btn-edit-template').forEach((btn) => {
    btn.addEventListener('click', () => {
      const t = calendarTemplates.find((x) => x.id === btn.dataset.id);
      if (t) showEditTemplatePopup(container, t, uocsList, refresh);
    });
  });

  container.querySelectorAll('.btn-cessa-template').forEach((btn) => {
    btn.addEventListener('click', () => {
      const t = calendarTemplates.find((x) => x.id === btn.dataset.id);
      if (t) showCessationPopup(container, t, refresh);
    });
  });

  container.querySelectorAll('.btn-del-template').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const t = calendarTemplates.find((x) => x.id === btn.dataset.id);
      const nm = t
        ? ensurePlannerSlotName(t.category, t.name, t.timeSlot, t.activityType || 'clinico')
        : '';
      const wd = t ? formatTemplateWeekdaysShort(t) : '';
      const period = t ? formatTemplatePeriod(t) : '';
      if (
        t &&
        t.dateFrom &&
        shiftPlannerLockedWeeksTouchRange(t.dateFrom, t.dateTo || t.dateFrom)
      ) {
        alert(SHIFT_PLANNER_LOCKED_CALENDAR_MSG);
        return;
      }
      const msg =
        t &&
        confirm(
          `Rimuovere lo slot ricorrente «${nm}» (${wd}, ${period}, ${TIME_SLOT_LABELS[t.timeSlot] || t.timeSlot})? ` +
            `Verranno eliminati tutti i giorni definiti da questo template; non è reversibile.`,
        );
      if (!msg) return;
      try {
        await calendarDeleteTemplate(btn.dataset.id);
        renderCalendarPlanner(container);
      } catch (e) {
        alert('Errore: ' + e.message);
      }
    });
  });

  if (!options.resetNewTemplateForm) {
    restoreRecurringTemplateFormDraft(container, formDraft);
  }
}
